// facilities--frame-prefetch — starts loading an Edit drawer's form as soon as
// the operator shows intent, so it is usually there by the time they click.
//
// The facilities admin Edit triggers (CONEK-7260) are links that target the
// Edit drawer's turbo frame, so the form is fetched on click and the panel
// waited on the round trip. The Add form is rendered with the page instead
// (CONEK-7405); a form per row cannot be, so Edit starts the same request
// early: on hover or keyboard focus of the trigger, it points the (still
// hidden) frame at the trigger's href and Turbo loads it there.
//
// On click, if the frame already holds — or is already loading — this very
// URL, the click's default is prevented so Turbo does not fetch it a second
// time; ui--drawer-opener#open still runs and shows the drawer. Otherwise
// (touch, no hover) the link behaves as before and Turbo fetches on click.
//
// Whenever the frame is pointed at a DIFFERENT record, it is first put back to
// its skeleton. Without that, the form of the row hovered before would stay on
// screen until the new one arrived, and a Save in that window would update the
// wrong record.
//
// Guards:
//   * `enabled` is rendered false for a user who may not update the record, so
//     hovering down a table does not fire a 403 per row;
//   * the prefetch URL carries `prefetch=1`, which the facilities admin
//     controllers exclude from adoption read tracking — a hover is not an
//     intentional read. A plain click (no prefetch) is tracked as before;
//   * a modified click (new tab) is left to the browser: no drawer.
//
// facilities--drawer-frame clears the frame's `src` when the drawer closes and
// when a prefetch fails, so the next intent always refetches.
import { Controller } from "@hotwired/stimulus"

import { frameSource, isModifiedClick, restorePristine } from "./pristine_frame"

export default class extends Controller {
  static values = { frameId: String, enabled: { type: Boolean, default: true } }

  prefetch() {
    if (!this.enabledValue) return

    const frame = this.#frame()
    if (!frame || this.#holds(frame)) return

    restorePristine(frame)
    frame.src = this.#prefetchUrl()
  }

  click(event) {
    if (isModifiedClick(event)) {
      event.stopImmediatePropagation()
      return
    }

    const frame = this.#frame()
    if (!frame) return

    if (this.#holds(frame)) {
      event.preventDefault()
    } else {
      restorePristine(frame)
    }
  }

  #frame() {
    return document.getElementById(this.frameIdValue)
  }

  #prefetchUrl() {
    const url = new URL(this.element.href)
    url.searchParams.set("prefetch", "1")
    return url.href
  }

  // The frame holds this form whether it was prefetched (marked URL) or
  // fetched by an earlier plain click (the href itself).
  #holds(frame) {
    const source = frameSource(frame)
    return source === this.#prefetchUrl() || source === this.element.href
  }
}
